export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function minutesToTime(m: number): string {
  const h = Math.floor(m / 60) % 24;
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function timeToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function clock(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function dayLabel(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

export function relative(ts: number, now = Date.now()): string {
  const diff = Math.round((ts - now) / 60_000);
  const abs = Math.abs(diff);
  if (abs < 1) return 'now';
  const text = abs < 60 ? `${abs} min` : abs < 1440 ? `${Math.round(abs / 60)} h` : `${Math.round(abs / 1440)} d`;
  return diff > 0 ? `in ${text}` : `${text} ago`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export function platformName(): string {
  const ua = navigator.userAgent;
  if (/CrOS/.test(ua)) return 'Chromebook';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Computer';
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** '06-15' -> 'June 15'. */
export function monthDayLabel(s: string): string {
  const [month, day] = s.split('-').map(Number);
  return `${MONTH_NAMES[(month ?? 1) - 1]} ${day}`;
}

/** What the child's schedule state looks like in a few words. */
export function policyLabel(p: {
  schoolTime: boolean;
  paused?: boolean;
  pauseEnd?: string;
  doneForDay?: boolean;
  onBreak: boolean;
  breakUntil: number;
  nextChange: number | null;
}): string {
  if (p.doneForDay) return 'Done for today';
  if (p.paused) return p.pauseEnd ? `School paused through ${monthDayLabel(p.pauseEnd)}` : 'School paused';
  if (p.onBreak) return `On a break, back ${relative(p.breakUntil)}`;
  if (p.schoolTime) return p.nextChange ? `School time, ends ${clock(p.nextChange)}` : 'School time';
  return p.nextChange ? `Free time, school starts ${dayAndClock(p.nextChange)}` : 'Free time';
}

function dayAndClock(ts: number): string {
  const label = dayLabel(ts);
  return label === 'Today' ? `at ${clock(ts)}` : `${label === 'Tomorrow' ? 'tomorrow' : label} ${clock(ts)}`;
}

/**
 * The secondary line for an activity row. Chat rows store an internal sender marker in `detail`
 * ('child', or 'parent:<name>'), which shouldn't be shown raw (#235).
 */
export function activityDetail(kind: string, detail: string): string {
  if (kind !== 'chat') return detail;
  return detail.startsWith('parent:') ? `Sent by ${detail.slice(7)}` : '';
}
